import { VersionRange } from '../../models/core/version-range.js';
import { DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../../models/dto/dto.js';
import { PhotoCollection } from '../../models/serializable/photo-collection.js';
import { MapUtils } from '../../utility/map-utils.js';
import { RenderableComponent } from '../core/component.js';

export class GalleryComponent extends RenderableComponent {
  static readonly TYPE_ID: string = 'f4b61437496e4f838ea7f6fc7037b070';
  static readonly SCHEMA_VERSION: number = 2;
  static readonly DEFAULT_RENDER_ORDER: number = 2;

  constructor(
      public readonly collection: PhotoCollection,
      public readonly renderOrder: number,
      public readonly isRenderingEnabled: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return GalleryComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, GalleryComponent.SCHEMA_VERSION);
  }

  get $removable(): boolean {
    return true;
  }

  static fromMap(context: SerializationContext, map: SerializedData): GalleryComponent {
    return new GalleryComponent(
        context.fromData<PhotoCollection>(MapUtils.get<SerializedData>(map, 'collection')),
        MapUtils.get<number>(map, 'renderOrder'),
        MapUtils.get<boolean>(map, 'isRenderingEnabled'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'collection': context.toData(this.collection),
      'renderOrder': this.renderOrder,
      'isRenderingEnabled': this.isRenderingEnabled,
    };
  }

  clone(): this {
    return new GalleryComponent(
        this.collection?.clone() ?? null,
        this.renderOrder,
        this.isRenderingEnabled,
    ) as this;
  }

  isSame(other: GalleryComponent): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.collection, this.collection) &&
          other.renderOrder === this.renderOrder &&
          other.isRenderingEnabled === this.isRenderingEnabled;
  }
}

export class GalleryComponentBuilder extends DataModelBuilder<GalleryComponent> {
  build(context: SerializationContext, map: SerializedData): GalleryComponent {
    return GalleryComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(GalleryComponent.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(GalleryComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'renderOrder', GalleryComponent.DEFAULT_RENDER_ORDER);
      MapUtils.setIfNullOrUndefined(map, 'isRenderingEnabled', true);
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
